import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createDealerUser } from '../../services/dealerUserService'
import { apiError } from '../../api/apiError'
import type { DealerUser } from '../../types'
import '../BranchForm/BranchFormModal.css'

interface Props {
  branchId: string
  onClose: () => void
  onSaved: (u: DealerUser) => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function DealerUserModal({ branchId, onClose, onSaved }: Props) {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  function validate() {
    const e: Record<string, string> = {}
    if (!EMAIL_RE.test(email.trim())) e.email = t('admin.dealer_users.invalid_email')
    if (!firstName.trim()) e.firstName = t('admin.dealer_users.required')
    if (!lastName.trim()) e.lastName = t('admin.dealer_users.required')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const created = await createDealerUser(branchId, {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      })
      onSaved(created)
    } catch (err) {
      const { code, message } = apiError(err)
      if (code === 'EMAIL_TAKEN') setErrors({ email: t('admin.dealer_users.error_email_taken') })
      else if (code === 'BRANCH_ARCHIVED') setErrors({ _: t('admin.dealer_users.error_branch_archived') })
      else setErrors({ _: message || t('admin.dealer_users.create_failed') })
    } finally {
      setLoading(false)
    }
  }

  function field(key: string, label: string, value: string, setValue: (v: string) => void, type = 'text') {
    return (
      <div className="modal__field">
        <label className="modal__label">{label}</label>
        <input
          className={`admin-input modal__input${errors[key] ? ' admin-input--error' : ''}`}
          type={type}
          value={value}
          autoComplete="off"
          onChange={(e) => { setValue(e.target.value); setErrors((p) => ({ ...p, [key]: '' })) }}
          disabled={loading}
        />
        {errors[key] && <span className="modal__error">{errors[key]}</span>}
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title">{t('admin.dealer_users.modal_title')}</h2>
          <button className="modal__close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <form className="modal__body" onSubmit={handleSubmit} noValidate>
          {field('email', t('admin.dealer_users.label_email'), email, setEmail, 'email')}
          {field('firstName', t('admin.dealer_users.label_first_name'), firstName, setFirstName)}
          {field('lastName', t('admin.dealer_users.label_last_name'), lastName, setLastName)}
          <p className="dealer-users__hint">{t('admin.dealer_users.invite_hint')}</p>

          {errors._ && <p className="modal__error">{errors._}</p>}

          <div className="modal__footer">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={onClose} disabled={loading}>
              {t('admin.dealer_users.cancel')}
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={loading}>
              {loading ? t('admin.dealer_users.sending') : t('admin.dealer_users.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
